import React from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { UserRoles } from 'Interfaces/User';
import AuthenticatedWrapper from 'Layouts/AuthenticatedWrapper';
import BasePathWrapper from 'Layouts/BasePathWrapper';
import DocumentPreviewWrapper from 'Layouts/DocumentPreviewWrapper';
import SettingsWrapper from 'Layouts/SettingsWrapper';
import SimplifiedWrapper from 'Layouts/SimplifiedWrapper';
import SettingsGuardLayout from 'Layouts/sublayouts/SettingsGuardLayout';
import UserAuthenticatedWrapper from 'Layouts/UserAuthenticatedWrapper';
import * as Auth from 'Pages/Auth';
import * as DocumentPreview from 'Pages/DocumentPreview';
import * as Documents from 'Pages/Documents';
import * as FormRequests from 'Pages/FormRequests';
import * as Integrations from 'Pages/Integrations';
import * as Settings from 'Pages/Settings';
import * as Sign from 'Pages/Sign';
import * as SignatureRequests from 'Pages/SignatureRequests';
import * as TeamPages from 'Pages/Team';
import * as Templates from 'Pages/Templates';
import * as Trash from 'Pages/Trash';
import * as Upsell from 'Pages/Upsell';

import BaseRoutes from './BaseRoutes';
import CustomRedirect from './CustomRedirect';

const slugRoutes = {
  trial: [
    AuthorizedRoutePaths.TRIAL_SUCCESS,
    AuthorizedRoutePaths.TRIAL_FROM_FREE_SUCCESS,
  ],
  purchase: [
    AuthorizedRoutePaths.UPGRADE_PERSONAL_SUCCESS,
    AuthorizedRoutePaths.UPGRADE_BUSINESS_SUCCESS,
    AuthorizedRoutePaths.START_PERSONAL_SUCCESS,
    AuthorizedRoutePaths.START_BUSINESS_SUCCESS,
  ],
};

const AuthorizedRoutes = createBrowserRouter([
  {
    path: '/',
    element: <UserAuthenticatedWrapper />,
    children: [
      { index: true, element: <CustomRedirect to={AuthorizedRoutePaths.SIGN} isAuth /> },
      ...BaseRoutes,
      {
        path: AuthorizedRoutePaths.UPSELL,
        element: <SimplifiedWrapper />,
        children: [{ index: true, element: <Upsell.Upsell /> }],
      },
      {
        path: AuthorizedRoutePaths.DOCUMENT_PREVIEW,
        element: <DocumentPreviewWrapper />,
        children: [{ index: true, element: <DocumentPreview.DocumentPreview /> }],
      },
      {
        path: AuthorizedRoutePaths.TRY_TRIAL,
        element: <AuthenticatedWrapper />,
        children: [{ index: true, element: <Auth.TrialGetPage /> }],
      },
      ...slugRoutes.trial.map(path => ({
        path,
        element: <AuthenticatedWrapper />,
        children: [{ index: true, element: <Auth.TrialSuccess /> }],
      })),
      ...slugRoutes.purchase.map(path => ({
        path,
        element: <AuthenticatedWrapper />,
        children: [{ index: true, element: <Auth.ChangePlanSuccess /> }],
      })),
      {
        path: AuthorizedRoutePaths.BASE_PATH,
        element: <BasePathWrapper />,
        children: [
          {
            index: true,
            element: <CustomRedirect to={AuthorizedRoutePaths.SIGN} isAuth />,
          },
          { path: AuthorizedRoutePaths.SIGN, element: <Sign.Dashboard /> },
          {
            path: AuthorizedRoutePaths.INTEGRATIONS,
            element: <Integrations.Integrations />,
          },
          { path: AuthorizedRoutePaths.TEAM, element: <TeamPages.TeamScreen /> },
          {
            path: AuthorizedRoutePaths.DOCUMENTS,
            children: [
              {
                path: AuthorizedRoutePaths.DOCUMENTS_STATUS,
                element: <Documents.Documents />,
              },
              {
                path: AuthorizedRoutePaths.RECEIVED_DOCUMENTS,
                element: <SignatureRequests.SignatureRequestsScreen />,
              },
              { path: AuthorizedRoutePaths.TRASH, element: <Trash.TrashScreen /> },
              { path: AuthorizedRoutePaths.DOCUMENTS_EDIT, element: <Sign.Edit /> },
            ],
          },
          {
            path: AuthorizedRoutePaths.TEMPLATES,
            children: [
              {
                index: true,
                element: (
                  <CustomRedirect to={AuthorizedRoutePaths.TEMPLATES_ACTIVE} isAuth />
                ),
              },
              {
                path: AuthorizedRoutePaths.TEMPLATES_STATUS,
                element: <Templates.TemplatesScreen />,
              },
              {
                path: AuthorizedRoutePaths.TEMPLATES_CREATE,
                element: <Templates.TemplateCreate />,
              },
              {
                path: AuthorizedRoutePaths.TEMPLATES_EDIT,
                element: <Templates.TemplateEdit />,
              },
            ],
          },
          {
            path: AuthorizedRoutePaths.FORM_REQUESTS,
            children: [
              {
                path: AuthorizedRoutePaths.FORM_REQUESTS_STATUS,
                element: <FormRequests.FormRequestsScreen />,
              },
              {
                path: AuthorizedRoutePaths.FORM_REQUESTS_CREATE,
                element: <FormRequests.FormRequestCreate />,
              },
              {
                path: AuthorizedRoutePaths.FORM_REQUESTS_EDIT,
                element: <FormRequests.FormRequestEdit />,
              },
            ],
          },
          {
            path: AuthorizedRoutePaths.SETTINGS,
            element: <SettingsWrapper />,
            children: [
              {
                index: true,
                element: (
                  <CustomRedirect to={AuthorizedRoutePaths.SETTINGS_COMPANY} isAuth />
                ),
              },
              {
                path: AuthorizedRoutePaths.SETTINGS_COMPANY,
                element: (
                  <SettingsGuardLayout
                    element={<Settings.Company />}
                    guard={({ role }) =>
                      role === UserRoles.OWNER || role === UserRoles.ADMIN
                    }
                  />
                ),
              },
              {
                path: AuthorizedRoutePaths.SETTINGS_PROFILE,
                element: <Settings.Profile />,
              },
              {
                path: AuthorizedRoutePaths.SETTINGS_REQUEST_HISTORY,
                element: (
                  <SettingsGuardLayout
                    element={<Settings.RequestHistory />}
                    guard={({ role }) => role === UserRoles.OWNER}
                  />
                ),
              },
              {
                path: AuthorizedRoutePaths.SETTINGS_API,
                element: (
                  <SettingsGuardLayout
                    element={<Settings.Api />}
                    guard={({ role }) => role === UserRoles.OWNER}
                  />
                ),
              },
              {
                path: AuthorizedRoutePaths.SETTINGS_EDIT_SIGNATURE,
                element: <Settings.SignaturePage />,
              },
              {
                path: AuthorizedRoutePaths.SETTINGS_BILLING,
                element: (
                  <SettingsGuardLayout
                    element={<Settings.BillingMainScreen />}
                    guard={({ role }) => role === UserRoles.OWNER}
                  />
                ),
              },
              {
                path: AuthorizedRoutePaths.SETTINGS_BILLING_PLAN,
                element: (
                  <SettingsGuardLayout
                    element={<Settings.BillingPlan />}
                    guard={({ role }) => role === UserRoles.OWNER}
                  />
                ),
              },
              {
                path: '*',
                element: (
                  <CustomRedirect
                    to={AuthorizedRoutePaths.SETTINGS_COMPANY}
                    replace
                    isAuth
                  />
                ),
              },
            ],
          },
          {
            path: '*',
            element: <CustomRedirect to={AuthorizedRoutePaths.SIGN} isAuth />,
          },
        ],
      },
    ],
  },
]);

export default AuthorizedRoutes;
